import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icons } from "@/components/Icons";
import { resolveHelp } from "@/lib/help";

/**
 * Slide-out help drawer. Reads the active route from `useLocation`,
 * looks the help entry up in the registry, and renders it. The
 * matching is longest-prefix so an obscure sub-page inherits its
 * parent's help text by default — adding tailored help for a new
 * sub-page is just one entry in `lib/help.ts`.
 *
 * Open/close is controlled by the parent (Shell) so the same `?`
 * icon in the top bar drives it. Escape closes; clicks on the
 * scrim close.
 */
export default function HelpPanel({
  open, onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const loc = useLocation();
  const help = resolveHelp(loc.pathname);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="scrim"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(0,0,0,0.25)",
        display: "flex", justifyContent: "flex-end",
      }}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Help"
        style={{
          width: 380, maxWidth: "100%", height: "100%",
          background: "var(--warm-card)", borderLeft: "1px solid var(--warm-border)",
          padding: "20px 24px", overflowY: "auto",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.08)",
        }}
      >
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <div className="kicker" style={{ fontSize: 11 }}>Help</div>
            <h2 style={{ fontSize: 20, margin: 0 }}>{help.title}</h2>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close help">
            <Icons.X size={18} />
          </button>
        </div>

        <p style={{ fontSize: 14, lineHeight: 1.55, marginTop: 0 }}>{help.intro}</p>

        {help.bullets && help.bullets.length > 0 && (
          <ul style={{ paddingLeft: 18, fontSize: 13.5, lineHeight: 1.55, marginTop: 12 }}>
            {help.bullets.map((b, i) => <li key={i} style={{ marginBottom: 8 }}>{b}</li>)}
          </ul>
        )}

        {help.related && help.related.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div className="kicker" style={{ fontSize: 11, marginBottom: 6 }}>Related</div>
            <div className="col" style={{ gap: 4 }}>
              {help.related.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  onClick={onClose}
                  className="nav-link"
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", fontSize: 13, borderRadius: "var(--r-sm)" }}
                >
                  <Icons.ChevRight size={12} />
                  <span>{r.label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {help.docs && (
          <a
            href={help.docs}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary btn-sm"
            style={{ marginTop: 18 }}
          >
            <Icons.Info size={13} /> Read the full guide
          </a>
        )}

        <div className="muted" style={{ fontSize: 11.5, marginTop: 24, paddingTop: 12, borderTop: "1px solid var(--warm-border)" }}>
          Press <kbd>?</kbd> any time to reopen this panel. Suggestions for what should be in
          here? Hit the support link in the footer.
        </div>
      </aside>
    </div>
  );
}
